type ChatRecord = Record<string, any>;

export interface NormalizedChatMessage extends ChatRecord {
  id: number | string;
  room_id: number | string;
  sender_name: string;
  content: string;
  message_type: string;
  attachments: unknown[];
  reactions: unknown[];
  created_at: string;
}

interface NormalizeChatMessageOptions {
  source: string;
  fallbackRoomId?: number;
  allowMissingRoomId?: boolean;
}

const warnedShapeKeys = new Set<string>();

function isRecord(value: unknown): value is ChatRecord {
  return typeof value === 'object' && value !== null;
}

function looksLikeChatMessageRecord(value: ChatRecord): boolean {
  return (
    'id' in value ||
    'room_id' in value ||
    'content' in value ||
    'message_type' in value
  );
}

function unwrapChatMessageRecord(raw: ChatRecord): ChatRecord {
  if (looksLikeChatMessageRecord(raw)) return raw;
  const nestedKeys = ['message', 'data', 'result', 'payload'];
  for (const key of nestedKeys) {
    const nested = raw[key];
    if (isRecord(nested) && looksLikeChatMessageRecord(nested)) {
      return nested;
    }
  }
  return raw;
}

function normalizeMessageId(value: unknown): number | string | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const parsed = Number(trimmed);
    if (Number.isFinite(parsed)) return parsed;
    return trimmed;
  }
  return null;
}

function normalizeRoomId(value: unknown, fallbackRoomId?: number): number | null {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }
  if (typeof fallbackRoomId === 'number' && Number.isFinite(fallbackRoomId) && fallbackRoomId > 0) {
    return fallbackRoomId;
  }
  return null;
}

function normalizeCreatedAt(value: unknown): string {
  if (typeof value === 'string' && value.trim()) {
    return value;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    try {
      return new Date(value).toISOString();
    } catch {
      return new Date().toISOString();
    }
  }
  return new Date().toISOString();
}

function normalizeMessageType(value: unknown): string {
  const normalized = asString(value).trim();
  return normalized || 'text';
}

function payloadPreview(payload: unknown): string {
  try {
    const raw = JSON.stringify(payload);
    if (!raw) return String(payload);
    return raw.length > 700 ? `${raw.slice(0, 700)}...` : raw;
  } catch {
    const raw = String(payload);
    return raw.length > 700 ? `${raw.slice(0, 700)}...` : raw;
  }
}

export function logInvalidChatPayloadShape(source: string, reason: string, payload: unknown): void {
  const key = `${source}:${reason}`;
  if (warnedShapeKeys.has(key)) return;
  warnedShapeKeys.add(key);
  console.warn(`[chat] invalid payload shape: ${reason}`, {
    source,
    sample: payloadPreview(payload),
  });
}

export function asArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

export function asString(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
    return String(value);
  }
  return '';
}

export function asNullableString(value: unknown): string | null {
  if (value == null) return null;
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
    return String(value);
  }
  return null;
}

export function normalizeChatMessage(
  raw: unknown,
  options: NormalizeChatMessageOptions
): NormalizedChatMessage | null {
  if (!isRecord(raw)) {
    logInvalidChatPayloadShape(options.source, 'message payload is not an object', raw);
    return null;
  }
  const payload = unwrapChatMessageRecord(raw);

  const id = normalizeMessageId(payload.id);
  if (id == null) {
    logInvalidChatPayloadShape(options.source, 'message has invalid id', raw);
    return null;
  }

  const roomId = normalizeRoomId(payload.room_id, options.fallbackRoomId);
  if (roomId == null && !options.allowMissingRoomId) {
    logInvalidChatPayloadShape(options.source, 'message has invalid room_id', raw);
    return null;
  }
  if (roomId == null) {
    logInvalidChatPayloadShape(options.source, 'message has invalid room_id after fallback', raw);
    return null;
  }

  const normalized: NormalizedChatMessage = {
    ...payload,
    id,
    room_id: roomId,
    sender_name: asString(payload.sender_name),
    content: asString(payload.content),
    message_type: normalizeMessageType(payload.message_type),
    attachments: asArray(payload.attachments),
    reactions: asArray(payload.reactions),
    created_at: normalizeCreatedAt(payload.created_at),
  };
  if ('reply_to_preview' in payload) {
    normalized.reply_to_preview = asNullableString(payload.reply_to_preview);
  }

  return normalized;
}

export function normalizeChatMessageList(
  raw: unknown,
  options: NormalizeChatMessageOptions
): NormalizedChatMessage[] {
  if (!Array.isArray(raw)) {
    logInvalidChatPayloadShape(options.source, 'messages payload is not an array', raw);
    return [];
  }

  const normalized: NormalizedChatMessage[] = [];
  for (const item of raw) {
    const msg = normalizeChatMessage(item, options);
    if (msg) normalized.push(msg);
  }
  return normalized;
}

export function normalizeChatSearchResults(raw: unknown, roomId: number): NormalizedChatMessage[] {
  return normalizeChatMessageList(raw, {
    source: `api.searchMessages(room:${roomId})`,
    fallbackRoomId: roomId,
    allowMissingRoomId: true,
  });
}

export function normalizeChatMessagePatch(
  raw: unknown,
  source: string
): { id: number | string; patch: ChatRecord } | null {
  if (!isRecord(raw)) {
    logInvalidChatPayloadShape(source, 'patch payload is not an object', raw);
    return null;
  }

  const id = normalizeMessageId(raw.id);
  if (id == null) {
    logInvalidChatPayloadShape(source, 'patch has invalid id', raw);
    return null;
  }

  const patch = sanitizeChatMessagePatch(raw);
  if ('is_edited' in raw) patch.is_edited = Boolean(raw.is_edited);
  if ('link_preview' in raw) patch.link_preview = isRecord(raw.link_preview) ? raw.link_preview : null;

  return { id, patch };
}

export function sanitizeChatMessagePatch(raw: unknown): ChatRecord {
  if (!isRecord(raw)) return {};
  const patch: ChatRecord = { ...raw };
  if ('sender_name' in raw) patch.sender_name = asString(raw.sender_name);
  if ('content' in raw) patch.content = asString(raw.content);
  if ('attachments' in raw) patch.attachments = asArray(raw.attachments);
  if ('reactions' in raw) patch.reactions = asArray(raw.reactions);
  if ('created_at' in raw) patch.created_at = normalizeCreatedAt(raw.created_at);
  if ('message_type' in raw) patch.message_type = normalizeMessageType(raw.message_type);
  if ('reply_to_preview' in raw) patch.reply_to_preview = asNullableString(raw.reply_to_preview);
  if ('deleted_at' in raw) patch.deleted_at = asNullableString(raw.deleted_at);
  return patch;
}
